import { memo, useMemo } from "react";
import { format, parseISO } from "date-fns";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { calcRate, formatCount, formatRate } from "../constants/metrics";
import type {
  CompanyStatsTrend,
  TrendUnit,
} from "../types/orderAnalytics.types";

interface Series {
  key: string;
  label: string;
  color: string;
}

// 계열 색은 지표에 고정한다(순서·색 바꾸지 말 것). 인접 계열끼리 색각 이상에서도 구분되는 순서로 검증된 팔레트.
const COUNT_SERIES: Series[] = [
  { key: "registCargoCnt", label: "등록 화물", color: "#2a78d6" },
  { key: "orderCnt", label: "오더 (취소 제외)", color: "#eb6834" },
  { key: "totalDispatchCnt", label: "총 배차", color: "#1baf7a" },
  { key: "infoNetUsingCnt", label: "정보망 사용", color: "#eda100" },
  { key: "progDispatchCnt", label: "프로그램 배차", color: "#e87ba4" },
];

const RATE_SERIES: Series[] = [
  { key: "progDispatchRate", label: "프로그램 배차율", color: "#2a78d6" },
  { key: "infoNetSuccessRate", label: "정보망 사용 성공률", color: "#eb6834" },
  { key: "callpassRegistRate", label: "콜패스 등록률", color: "#1baf7a" },
];

const DAU_SERIES: Series[] = [
  { key: "avgDau", label: "평균 DAU", color: "#2a78d6" },
];

const AXIS_TICK = { fontSize: 11, fill: "#898781" };
const GRID_COLOR = "#e1e0d9";
const AXIS_COLOR = "#c3c2b7";

const BUCKET_LABEL_FORMAT: Record<TrendUnit, string> = {
  DAY: "MM-dd",
  WEEK: "MM-dd",
  MONTH: "yyyy-MM",
};

const BUCKET_COLUMN_LABEL: Record<TrendUnit, string> = {
  DAY: "일자",
  WEEK: "주 시작일",
  MONTH: "월",
};

const round1 = (value: number) => Math.round(value * 10) / 10;

interface TrendRow {
  label: string;
  avgDau: number;
  registCargoCnt: number;
  orderCnt: number;
  totalDispatchCnt: number;
  infoNetUsingCnt: number;
  progDispatchCnt: number;
  progDispatchRate: number;
  infoNetSuccessRate: number;
  callpassRegistRate: number;
}

interface TrendLineChartProps {
  title: string;
  rows: TrendRow[];
  series: Series[];
  formatValue: (value: number) => string;
  /** 건수 축은 정수 눈금만 쓴다 */
  allowDecimals?: boolean;
}

const TrendLineChart = memo(function TrendLineChart({
  title,
  rows,
  series,
  formatValue,
  allowDecimals = true,
}: TrendLineChartProps) {
  const hasDots = rows.length <= 14;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3">
      <h3 className="mb-2 text-xs font-medium text-gray-700">{title}</h3>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={GRID_COLOR} vertical={false} />
            <XAxis
              dataKey="label"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={{ stroke: AXIS_COLOR }}
              minTickGap={16}
            />
            <YAxis
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={44}
              allowDecimals={allowDecimals}
              tickFormatter={(value: number) => value.toLocaleString("ko-KR")}
            />
            <Tooltip
              formatter={(value) => formatValue(Number(value))}
              contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: GRID_COLOR }}
              labelStyle={{ color: "#52514e", marginBottom: 2 }}
              itemStyle={{ color: "#0b0b0b", padding: 0 }}
            />
            {series.length > 1 && (
              <Legend
                verticalAlign="top"
                height={24}
                iconType="plainline"
                itemSorter={null}
                formatter={(value) => (
                  <span style={{ fontSize: 11, color: "#52514e" }}>{value}</span>
                )}
              />
            )}
            {series.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                dot={hasDots ? { r: 3, strokeWidth: 0, fill: s.color } : false}
                activeDot={{ r: 4, stroke: "#ffffff", strokeWidth: 2 }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
});

const formatCountValue = (value: number) => `${formatCount(value)}건`;
const formatRateValue = (value: number) => `${formatRate(value)}%`;
const formatDauValue = (value: number) => formatCount(value, 1);

const TABLE_COLUMNS: { key: keyof TrendRow; label: string; format: (v: number) => string }[] = [
  { key: "avgDau", label: "평균 DAU", format: formatDauValue },
  { key: "registCargoCnt", label: "등록 화물", format: formatCount },
  { key: "orderCnt", label: "오더", format: formatCount },
  { key: "totalDispatchCnt", label: "총 배차", format: formatCount },
  { key: "infoNetUsingCnt", label: "정보망 사용", format: formatCount },
  { key: "progDispatchCnt", label: "프로그램 배차", format: formatCount },
  { key: "progDispatchRate", label: "프로그램 배차율", format: formatRateValue },
  { key: "infoNetSuccessRate", label: "정보망 성공률", format: formatRateValue },
  { key: "callpassRegistRate", label: "콜패스 등록률", format: formatRateValue },
];

interface TrendChartsProps {
  trend: CompanyStatsTrend;
}

export const TrendCharts = memo(function TrendCharts({ trend }: TrendChartsProps) {
  const rows = useMemo<TrendRow[]>(
    () =>
      trend.points.map((p) => ({
        label: format(parseISO(p.bucketStart), BUCKET_LABEL_FORMAT[trend.unit]),
        avgDau: p.avgDau,
        registCargoCnt: p.registCargoCnt,
        orderCnt: p.orderCnt,
        totalDispatchCnt: p.totalDispatchCnt,
        infoNetUsingCnt: p.infoNetUsingCnt,
        progDispatchCnt: p.progDispatchCnt,
        progDispatchRate: round1(calcRate(p.progDispatchCnt, p.totalDispatchCnt)),
        infoNetSuccessRate: round1(calcRate(p.progDispatchCnt, p.infoNetUsingCnt)),
        callpassRegistRate: round1(calcRate(p.infoNetUsingCnt, p.orderCnt)),
      })),
    [trend],
  );

  return (
    <div className="space-y-3">
      <TrendLineChart
        title="건수 추이 (건)"
        rows={rows}
        series={COUNT_SERIES}
        formatValue={formatCountValue}
        allowDecimals={false}
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TrendLineChart
          title="비율 추이 (%)"
          rows={rows}
          series={RATE_SERIES}
          formatValue={formatRateValue}
        />
        <TrendLineChart
          title="평균 DAU 추이"
          rows={rows}
          series={DAU_SERIES}
          formatValue={formatDauValue}
        />
      </div>

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="max-h-[220px] overflow-auto">
          <table className="w-full">
            <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="h-8 px-3 text-left text-xs font-medium whitespace-nowrap text-gray-500">
                  {BUCKET_COLUMN_LABEL[trend.unit]}
                </th>
                {TABLE_COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className="h-8 px-3 text-right text-xs font-medium whitespace-nowrap text-gray-500"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.label} className="h-8 hover:bg-gray-50">
                  <td className="px-3 text-xs whitespace-nowrap text-gray-700 tabular-nums">
                    {row.label}
                  </td>
                  {TABLE_COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      className="px-3 text-right text-xs whitespace-nowrap text-gray-700 tabular-nums"
                    >
                      {col.format(row[col.key] as number)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
});
